"use client";
import { ChevronDown, CirclePlus, Pencil, User, Users } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "../../elements/button/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../elements/select/select";
import { Switch } from "../../elements/switch/switch";
import { Text } from "../../elements/text/text";

export interface UserAccount {
  id: string;
  firstName: string;
  lastName: string;
  role: "Employee" | "Administrator" | "Sub Administrator" | "Viewer";
  status: "Active" | "Inactive";
  email?: string;
  isDisabled?: boolean;
  onEdit?: () => void;
  onRoleChange?: (role: string) => void;
  onStatusChange?: (isActive: boolean) => void;
}

export interface UserMyCompanyProps {
  microcopies: {
    title: string;
    addNewUserLabel: string;
    firstNameHeader: string;
    lastNameHeader: string;
    roleHeader: string;
    statusHeader: string;
    editLabel: string;
    customerIdLabel: string;
    emailLabel: string;
    emptyStateLabel: string;
    toggleDetailsAriaLabel: string;
    statusAriaLabel: string;
    roleLabels?: Record<string, string>;
    statusLabels?: Record<string, string>;
  };
  users: UserAccount[];
  roleOptions?: string[];
  onAddNewUser?: () => void;
}

function formatUserTemplate(
  template: string,
  user: Pick<UserAccount, "firstName" | "lastName">
): string {
  return template
    .replace("{firstName}", user.firstName)
    .replace("{lastName}", user.lastName);
}

export function UserMyCompany(props: Readonly<UserMyCompanyProps>): ReactNode {
  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(
    new Set()
  );

  const toggleExpanded = (userId: string) => {
    const newExpanded = new Set(expandedUserIds);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUserIds(newExpanded);
  };

  return (
    <div className="w-full rounded-md bg-white p-4">
      {/* Header Section */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users aria-hidden="true" className="size-5 text-muted-foreground" />
          <Text variant="h4">{props.microcopies.title}</Text>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={props.onAddNewUser}
          size="sm"
          variant="ghost"
        >
          <CirclePlus
            aria-hidden="true"
            className="size-4 text-muted-foreground"
          />
          <Text variant="body-sm-bold">
            {props.microcopies.addNewUserLabel}
          </Text>
        </Button>
      </div>

      {/* Table Section */}
      <div className="w-full overflow-hidden">
        {/* Desktop Header Row - hidden on mobile */}
        <div className="hidden border-border border-b bg-transparent px-4 py-3 md:grid md:grid-cols-[2.5rem_2.5rem_1fr_1fr_1fr_1fr_auto] md:gap-4">
          <div />
          <div />
          <div className="text-zinc-500">
            <Text variant="body-md">{props.microcopies.firstNameHeader}</Text>
          </div>
          <div className="text-zinc-500">
            <Text variant="body-md">{props.microcopies.lastNameHeader}</Text>
          </div>
          <div className="text-zinc-500">
            <Text variant="body-md">{props.microcopies.roleHeader}</Text>
          </div>
          <div className="text-zinc-500">
            <Text variant="body-md">{props.microcopies.statusHeader}</Text>
          </div>
          <div className="w-10" />
        </div>

        {/* User Rows */}
        {props.users.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Text variant="body-md">{props.microcopies.emptyStateLabel}</Text>
          </div>
        ) : (
          props.users.map((user) => {
            const isExpanded = expandedUserIds.has(user.id);
            return (
              <div className="border-border border-b" key={user.id}>
                {/* Desktop Row */}
                <div className="hidden px-4 pt-3 pb-2 hover:bg-secondary md:grid md:grid-cols-[2.5rem_2.5rem_1fr_1fr_1fr_1fr_auto] md:gap-4">
                  {/* Accordion Toggle */}
                  <div className="flex items-center">
                    <Button
                      aria-expanded={isExpanded}
                      aria-label={formatUserTemplate(
                        props.microcopies.toggleDetailsAriaLabel,
                        user
                      )}
                      className="p-0"
                      onClick={() => toggleExpanded(user.id)}
                      size="sm"
                      variant="ghost"
                    >
                      <ChevronDown
                        aria-hidden="true"
                        className={`size-4 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </Button>
                  </div>

                  {/* User Icon */}
                  <div className="flex items-center">
                    <User
                      aria-hidden="true"
                      className="size-5 text-muted-foreground"
                    />
                  </div>

                  {/* First Name */}
                  <div className="flex items-center text-left font-medium">
                    <Text variant="body-md">{user.firstName}</Text>
                  </div>

                  {/* Last Name */}
                  <div className="flex items-center pl-1 text-left font-medium">
                    <Text variant="body-md">{user.lastName}</Text>
                  </div>

                  {/* Role Select */}
                  <div className="flex items-center">
                    <Select
                      disabled={user.isDisabled}
                      onValueChange={user.onRoleChange}
                      value={user.role}
                    >
                      <SelectTrigger className="w-full rounded border border-border bg-white px-3 py-2 hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {props.roleOptions?.map((role) => (
                          <SelectItem key={role} value={role}>
                            {props.microcopies.roleLabels?.[role] ?? role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status Switch */}
                  <div className="flex items-center justify-start gap-2">
                    <Switch
                      aria-label={formatUserTemplate(
                        props.microcopies.statusAriaLabel,
                        user
                      )}
                      checked={user.status === "Active"}
                      className="data-[state=checked]:bg-black [&_span]:bg-white"
                      disabled={user.isDisabled}
                      onCheckedChange={user.onStatusChange}
                    />
                    <div>
                      <Text variant="body-sm">
                        {props.microcopies.statusLabels?.[user.status] ??
                          user.status}
                      </Text>
                    </div>
                  </div>

                  {/* Edit Button */}
                  <div className="flex items-center justify-end text-muted-foreground">
                    <Button
                      aria-label={`${props.microcopies.editLabel} ${user.firstName} ${user.lastName}`}
                      disabled={user.isDisabled}
                      onClick={user.onEdit}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil
                        aria-hidden="true"
                        className="size-6 text-zinc-500"
                      />
                    </Button>
                  </div>
                </div>

                {/* Mobile Row */}
                <div className="py-4 md:hidden">
                  {/* Row 1: toggle + name + edit */}
                  <div className="flex items-center gap-3">
                    <Button
                      aria-expanded={isExpanded}
                      aria-label={formatUserTemplate(
                        props.microcopies.toggleDetailsAriaLabel,
                        user
                      )}
                      className="p-0"
                      onClick={() => toggleExpanded(user.id)}
                      size="sm"
                      variant="ghost"
                    >
                      <ChevronDown
                        aria-hidden="true"
                        className={`size-4 text-muted-foreground transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </Button>
                    <User
                      aria-hidden="true"
                      className="size-5 text-muted-foreground"
                    />
                    <div className="flex-1 font-medium">
                      <Text variant="body-sm">
                        {user.firstName} {user.lastName}
                      </Text>
                    </div>
                    <Button
                      aria-label={`${props.microcopies.editLabel} ${user.firstName} ${user.lastName}`}
                      disabled={user.isDisabled}
                      onClick={user.onEdit}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil
                        aria-hidden="true"
                        className="size-4 text-muted-foreground"
                      />
                    </Button>
                  </div>

                  {/* Row 2: Role dropdown */}
                  <div className="mt-2 w-44 pl-8">
                    <Select
                      disabled={user.isDisabled}
                      onValueChange={user.onRoleChange}
                      value={user.role}
                    >
                      <SelectTrigger className="h-9 w-full rounded border border-border bg-white px-3 py-2 hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {props.roleOptions?.map((role) => (
                          <SelectItem key={role} value={role}>
                            {props.microcopies.roleLabels?.[role] ?? role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Row 3: Status switch */}
                  <div className="mt-2 flex items-center gap-2 pl-10">
                    <Switch
                      aria-label={formatUserTemplate(
                        props.microcopies.statusAriaLabel,
                        user
                      )}
                      checked={user.status === "Active"}
                      className="data-[state=checked]:bg-black [&_span]:bg-white"
                      disabled={user.isDisabled}
                      onCheckedChange={user.onStatusChange}
                    />
                    <Text variant="body-sm">
                      {props.microcopies.statusLabels?.[user.status] ??
                        user.status}
                    </Text>
                  </div>
                </div>

                {/* Expandable Content */}
                {isExpanded && (
                  <div className="py-2 pl-10 md:grid md:grid-cols-[2.5rem_2.5rem_1fr_1fr_1fr_1fr_auto] md:gap-4 md:pl-0">
                    <div className="hidden md:block" />
                    <div className="hidden md:block" />
                    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 px-4 md:col-span-4">
                      <dt className="font-semibold">
                        <Text variant="body-sm">
                          {props.microcopies.customerIdLabel}
                        </Text>
                      </dt>
                      <dd>
                        <Text variant="body-sm">{user.id}</Text>
                      </dd>
                      <dt className="font-semibold">
                        <Text variant="body-sm">
                          {props.microcopies.emailLabel}
                        </Text>
                      </dt>
                      <dd>
                        <Text variant="body-sm">{user.email || "—"}</Text>
                      </dd>
                    </dl>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
